// Boost Blenz Admin — "New Stuff": decision tools that tie a spend or a new
// product back to REAL cash. Two calculators + saved scenarios (localStorage in
// this prototype; a real build would persist to D1 so it's shared + durable).
//   • Can we afford it?  — model a one-time purchase (machine, tooling, …) vs the
//     cash on hand + a min buffer, incl. deposit/balance terms and time-to-afford.
//   • New Product        — unit economics + upfront launch cost → margin, break-even,
//     payback, and whether the launch is affordable now.
// PROTOTYPE: clickable end-to-end so the owner can decide whether to build it for real.

import React from "react";
import { OSWALD, FIGTREE, useT, useIsMobile } from "../theme";
import { Icon, Title, Card } from "../atoms";
import { FitGroup, FitValue, ConfirmDialog } from "./finance";
import { useCachedResource } from "../datacache";

const money = (n: number, dp = 2) => (n < 0 ? '-$' : '$') + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });

// ---- live cash (same source + cache key as the Daily Pulse Cash card, so opening
// New Stuff right after Daily Pulse paints the balance instantly) ------------
type Cash = { total: number; buckets: { name: string; balance: number }[] } | null;
function useCash(): { cash: Cash; loading: boolean } {
  const res = useCachedResource<any>('/api/cash', async () => {
    const r = await fetch('/api/cash');
    if (!r.ok) throw new Error(String(r.status));
    return await r.json();
  });
  const d = res.data;
  const cash: Cash = d?.ok && d.bank ? { total: d.bank.total, buckets: d.bank.buckets || [] } : null;
  return { cash, loading: res.loading };
}

// ---- saved scenarios (persisted to the account: D1 via /api/newstuff) -------
// A row stores ONLY the inputs that were typed. Affordability is never read back
// from it — opening a scenario recomputes the verdict against today's balance,
// so an old model can't keep claiming to be affordable on stale cash. savedCash
// / savedVerdict ride along purely to explain what changed since the save.
type Scenario = {
  id: string; type: 'afford' | 'product'; title: string; inputs: any;
  savedCash: number | null; savedVerdict: string | null;
  createdAt: string; createdBy: string | null;
};

// SQLite hands back "YYYY-MM-DD HH:MM:SS" in UTC — parse it as UTC explicitly so
// the saved date doesn't shift by the timezone offset.
const parseAt = (s: string) => new Date(/[TZ]/.test(s) ? s : s.replace(' ', 'T') + 'Z');
const stamp = (ms: number) => new Date(ms).toISOString().slice(0, 19).replace('T', ' ');

const LEGACY_KEY = 'bb-newstuff-scenarios-v1';   // pre-D1 browser-local store
const MIGRATED_KEY = 'bb-newstuff-migrated-v1';

function useScenarios() {
  const [list, setList] = React.useState<Scenario[] | null>(null);
  const [err, setErr] = React.useState(false);

  const get = React.useCallback(async (): Promise<Scenario[] | null> => {
    try {
      const r = await fetch('/api/newstuff/scenarios');
      if (!r.ok) throw new Error('load');
      const j = await r.json();
      setErr(false);
      return (j.scenarios || []) as Scenario[];
    } catch { setErr(true); return null; }
  }, []);

  const post = React.useCallback(async (body: any): Promise<Scenario[] | null> => {
    try {
      const r = await fetch('/api/newstuff/scenarios', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
      if (!r.ok) throw new Error('save');
      const j = await r.json();
      setErr(false);
      return (j.scenarios || []) as Scenario[];
    } catch { setErr(true); return null; }
  }, []);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      const server = await get();
      if (!alive || server == null) return;
      // One-time lift of anything kept in this browser before scenarios moved to
      // the account, so nothing saved earlier is lost. Only runs when the account
      // has none of its own, and only once per browser.
      let final = server;
      if (server.length === 0 && !localStorage.getItem(MIGRATED_KEY)) {
        let legacy: any[] = [];
        try { legacy = JSON.parse(localStorage.getItem(LEGACY_KEY) || '[]'); } catch { legacy = []; }
        // oldest first so the account list keeps the original order
        for (const s of legacy.slice().reverse()) {
          if (!s?.title || !s?.type) continue;
          const res = await post({ type: s.type, title: s.title, inputs: s.inputs || {}, createdAt: s.savedAt ? stamp(s.savedAt) : undefined });
          if (res) final = res;
        }
        try { localStorage.setItem(MIGRATED_KEY, '1'); localStorage.removeItem(LEGACY_KEY); } catch { /* ignore */ }
      }
      if (alive) setList(final);
    })();
    return () => { alive = false; };
  }, [get, post]);

  const add = async (body: any) => { const res = await post(body); if (res) setList(res); return !!res; };
  const remove = async (id: string) => {
    try {
      const r = await fetch(`/api/newstuff/scenarios/${id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error('delete');
      const j = await r.json();
      setList((j.scenarios || []) as Scenario[]);
      setErr(false);
      return true;
    } catch { setErr(true); return false; }
  };
  return { list, err, add, remove };
}

// ---- form atoms (inset dark fields, per the redesign handoff) ---------------
const FIELD_BOX: any = { display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(13,10,30,0.6)', border: '1px solid rgba(150,130,230,0.2)', borderRadius: 11, padding: '12px 15px' };

function Field({ label, hint, children }: any) {
  const t = useT();
  return (
    <label style={{ display: 'block', minWidth: 0 }}>
      <span style={{ display: 'block', fontFamily: FIGTREE, fontWeight: 600, fontSize: 13, color: t.fg2, marginBottom: 8 }}>{label}</span>
      {children}
      {hint && <span style={{ display: 'block', fontFamily: FIGTREE, fontSize: 11.5, color: t.faint, marginTop: 7 }}>{hint}</span>}
    </label>
  );
}
function Num({ value, onChange, prefix = '$', suffix, step = 1 }: any) {
  const t = useT();
  return (
    <div style={FIELD_BOX}>
      {prefix && <span style={{ color: t.faint, fontFamily: FIGTREE, fontSize: 15 }}>{prefix}</span>}
      <input type="number" step={step} value={Number.isFinite(value) ? value : ''} onChange={(e) => onChange(e.target.value === '' ? 0 : parseFloat(e.target.value))}
        style={{ border: 'none', outline: 'none', background: 'transparent', color: t.fg, fontFamily: FIGTREE, fontWeight: 600, fontSize: 15, width: '100%', minWidth: 0, padding: 0, fontVariantNumeric: 'tabular-nums' }} />
      {suffix && <span style={{ color: t.faint, fontFamily: FIGTREE, fontSize: 13, whiteSpace: 'nowrap' }}>{suffix}</span>}
    </div>
  );
}
function Text({ value, onChange, placeholder }: any) {
  const t = useT();
  return (
    <div style={FIELD_BOX}>
      <input value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
        style={{ border: 'none', outline: 'none', background: 'transparent', color: t.fg, fontFamily: FIGTREE, fontSize: 14.5, width: '100%', minWidth: 0, padding: 0 }} />
    </div>
  );
}
function Select({ value, onChange, options }: any) {
  const t = useT();
  return (
    <div style={{ ...FIELD_BOX, position: 'relative', padding: 0 }}>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        style={{ appearance: 'none', WebkitAppearance: 'none', border: 'none', outline: 'none', background: 'transparent', color: t.fg, fontFamily: FIGTREE, fontSize: 14.5, width: '100%', minWidth: 0, padding: '12px 38px 12px 15px', cursor: 'pointer' }}>
        {options.map((o: any) => <option key={o.v} value={o.v} style={{ background: '#17122f' }}>{o.l}</option>)}
      </select>
      <Icon name="chevron" size={16} color="#8f85b5" style={{ position: 'absolute', right: 13, top: '50%', transform: 'translateY(-50%) rotate(90deg)', pointerEvents: 'none' }} />
    </div>
  );
}
// Segmented pill tabs — active tab is a solid orange pill, the rest are plain text.
function Seg({ value, onChange, options }: any) {
  const t = useT();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      {options.map((o: any) => {
        const on = value === o.v;
        return (
          <button key={o.v} onClick={() => onChange(o.v)} style={{
            cursor: 'pointer', border: 'none', borderRadius: 999, padding: on ? '10px 22px' : '10px 18px',
            fontFamily: FIGTREE, fontWeight: on ? 700 : 600, fontSize: 14,
            background: on ? t.accent : 'transparent', color: on ? '#1a1330' : t.muted, transition: 'color 160ms',
          }}>{o.l}{o.badge != null ? ` (${o.badge})` : ''}</button>
        );
      })}
    </div>
  );
}

// Card section heading (Oswald caps) inside the calculator cards.
function FormHead({ children, style }: any) {
  const t = useT();
  return <div style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 15, color: t.fg, marginBottom: 20, ...style }}>{children}</div>;
}

// verdict color helper
const V_TINT: any = {
  good: ['rgba(87,217,138,0.08)', 'rgba(87,217,138,0.55)', 'rgba(87,217,138,0.2)'],
  warn: ['rgba(255,206,90,0.08)', 'rgba(255,206,90,0.5)', 'rgba(255,206,90,0.2)'],
  bad: ['rgba(255,139,125,0.08)', 'rgba(255,139,125,0.5)', 'rgba(255,139,125,0.2)'],
};
function vColor(t: any, tone: string) { return tone === 'good' ? t.good : tone === 'warn' ? t.warn : tone === 'bad' ? t.bad : t.muted; }
function VerdictCard({ tone, title, detail }: any) {
  const t = useT(); const c = vColor(t, tone);
  const [bg, bd, chip] = V_TINT[tone] || V_TINT.warn;
  return (
    <div style={{ background: bg, border: '1.5px solid ' + bd, borderRadius: 14, padding: '18px 20px', marginBottom: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 8 }}>
        <span style={{ width: 24, height: 24, borderRadius: 999, background: chip, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon name={tone === 'good' ? 'check' : 'alert'} size={14} color={c} strokeWidth={3} />
        </span>
        <span style={{ fontFamily: FIGTREE, fontWeight: 800, fontSize: 18, color: t.fg }}>{title}</span>
      </div>
      {detail && <div style={{ fontFamily: FIGTREE, fontSize: 13.5, color: t.sidebarMuted, lineHeight: 1.6, marginLeft: 35 }}>{detail}</div>}
    </div>
  );
}
function Line({ k, v, last }: any) {
  const t = useT();
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '13px 0', borderBottom: last ? 'none' : '1px solid ' + t.border }}>
      <span style={{ fontFamily: FIGTREE, fontSize: 13.5, color: t.sidebarMuted }}>{k}</span>
      <b style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 14, color: t.fg, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{v}</b>
    </div>
  );
}
// Two-up quick reference tiles above the full breakdown (New product).
function QuickRef({ items }: any) {
  const t = useT();
  return (
    <FitGroup>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '12px 0 4px' }}>
        {items.map((it: any) => (
          <div key={it.label} style={{ flex: '1 1 120px', minWidth: 120, background: 'rgba(13,10,30,0.4)', border: '1px solid rgba(150,130,230,0.12)', borderRadius: 12, padding: '12px 14px' }}>
            <div style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 11, color: t.muted }}>{it.label}</div>
            <FitValue size={19} color={t.fg} style={{ marginTop: 3 }}>{it.value}</FitValue>
          </div>
        ))}
      </div>
    </FitGroup>
  );
}
// Shown when a SAVED scenario is opened and today's verdict differs from the one
// it was saved under. The live verdict always wins — this only explains why it
// moved, so a scenario can never quietly look affordable on old numbers.
function RecheckBanner({ seedMeta, verdict, cash }: any) {
  const t = useT();
  if (!seedMeta?.savedVerdict || !verdict || seedMeta.savedVerdict === verdict) return null;
  const when = seedMeta.savedAt ? parseAt(seedMeta.savedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null;
  const then = seedMeta.savedCash;
  const now = cash?.total;
  const worse = /^(not yet|thin|loses)/i.test(verdict);
  const c = worse ? t.warn : t.good;
  return (
    <div style={{ display: 'flex', gap: 12, background: 'rgba(29,23,64,0.55)', border: '1px solid rgba(150,130,230,0.16)', borderLeft: '3px solid ' + c, borderRadius: 14, padding: '14px 16px', marginBottom: 16 }}>
      <span style={{ flexShrink: 0, width: 28, height: 28, borderRadius: 8, background: worse ? t.warnSoft : t.goodSoft, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon name="refresh" size={15} color={c} strokeWidth={2.2} />
      </span>
      <div>
        <div style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: 10.5, color: c, marginBottom: 5 }}>Re-checked against today’s cash</div>
        <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.sidebarMuted, lineHeight: 1.6 }}>
          When you saved this{when ? ` on ${when}` : ''} it read <b style={{ color: '#e6dfff' }}>{seedMeta.savedVerdict}</b>
          {then != null && now != null ? <> — the bank held {money(then, 0)}, today it holds {money(now, 0)}.</> : '.'}
          {' '}The verdict above is computed from <b style={{ color: '#e6dfff' }}>today’s</b> balance.
        </div>
      </div>
    </div>
  );
}

function CashStrip({ cash, loading }: any) {
  const t = useT();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', background: 'rgba(13,10,30,0.5)', border: '1px solid rgba(150,130,230,0.14)', borderRadius: 14, padding: '16px 20px', marginBottom: 16 }}>
      <span style={{ width: 30, height: 30, borderRadius: 999, background: t.goodSoft, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: t.good, fontFamily: FIGTREE, fontWeight: 800, fontSize: 15, flexShrink: 0 }}>$</span>
      <span style={{ fontFamily: FIGTREE, fontSize: 14, color: t.sidebarMuted }}>Cash in bank now</span>
      <span style={{ fontFamily: FIGTREE, fontWeight: 800, fontSize: 22, color: t.good, fontVariantNumeric: 'tabular-nums' }}>{loading ? '…' : cash ? money(cash.total, 0) : '—'}</span>
      {cash && <span style={{ marginLeft: 'auto', fontFamily: FIGTREE, fontSize: 12, color: t.faint }}>{cash.buckets.length} Bluevine buckets · live from QuickBooks</span>}
    </div>
  );
}
function SaveBar({ title, setTitle, onSave, saved, label }: any) {
  const t = useT();
  const can = !!title.trim();
  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 16, paddingTop: 16, borderTop: '1px solid ' + t.border }}>
      <div style={{ flex: '1 1 180px', minWidth: 180 }}><Text value={title} onChange={setTitle} placeholder="Name this scenario to save it…" /></div>
      <button onClick={onSave} disabled={!can} style={{
        cursor: can ? 'pointer' : 'not-allowed', opacity: can ? 1 : 0.5, border: 'none', background: t.accent, color: '#1a1330',
        borderRadius: 11, padding: '13px 22px', fontFamily: FIGTREE, fontWeight: 700, fontSize: 12.5,
        textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap',
      }}>{saved ? 'Saved ✓' : label}</button>
    </div>
  );
}

// Shared two-column shell: inputs on the left, live cash + result on the right.
function ToolLayout({ isMobile, form, result }: any) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1.35fr) minmax(300px,1fr)', gap: 20, alignItems: 'start' }}>
      {form}
      <div style={{ minWidth: 0 }}>{result}</div>
    </div>
  );
}

// ---- Tool 1: Can we afford it? ---------------------------------------------
const AFFORD_DEFAULTS = { title: '', cost: 5000, pay: 'full', depositPct: 50, fundFrom: 'total', buffer: 10000, ongoing: 0, benefit: 0, allocPerWeek: 500 };
function AffordTool({ cash, loading, seed, seedMeta, onSave }: any) {
  const t = useT(); const isMobile = useIsMobile();
  const [f, setF] = React.useState<any>(seed || AFFORD_DEFAULTS);
  const [title, setTitle] = React.useState<string>(seed?.title || '');
  const [saved, setSaved] = React.useState(false);
  const set = (k: string, v: any) => { setF((p: any) => ({ ...p, [k]: v })); setSaved(false); };

  const bucketOpts = [{ v: 'total', l: 'Total cash (all buckets)' }, ...(cash?.buckets || []).map((b: any) => ({ v: b.name, l: b.name.replace(/^Bluevine\s*/i, '') + ' — ' + money(b.balance, 0) }))];
  const available = f.fundFrom === 'total' ? (cash?.total || 0) : (cash?.buckets?.find((b: any) => b.name === f.fundFrom)?.balance || 0);
  const dueNow = f.pay === 'full' ? f.cost : f.cost * (f.depositPct / 100);
  const afterNow = available - dueNow;
  const affordable = afterNow >= f.buffer;
  const needMore = f.buffer + dueNow - available; // >0 = short
  const weeks = needMore > 0 && f.allocPerWeek > 0 ? Math.ceil(needMore / f.allocPerWeek) : 0;
  const payback = f.benefit > 0 ? f.cost / f.benefit : null;
  const tone = !affordable ? 'bad' : (afterNow - f.buffer) < dueNow * 0.25 ? 'warn' : 'good';
  const vTitle = !affordable ? `Not yet — short ${money(needMore, 0)}` : tone === 'warn' ? 'Yes, but tight' : 'Yes — affordable now';
  // Leads with the item price rather than the cash-remaining figures (redesign
  // handoff) — the affordability math behind it is unchanged.
  const payWord = f.pay === 'full' ? 'paid in full' : `${f.depositPct}% deposit now (${money(dueNow, 0)})`;
  const priceLead = <b style={{ color: '#e6dfff' }}>Item price {money(f.cost, 0)}</b>;
  const vDetail = !affordable
    ? (weeks > 0
        ? <>{priceLead} · {payWord} — short {money(needMore, 0)} against your {money(f.buffer, 0)} floor. At {money(f.allocPerWeek, 0)}/wk you&apos;d be there in ~{weeks} week{weeks > 1 ? 's' : ''}, or free up {money(needMore, 0)} elsewhere.</>
        : <>{priceLead} · {payWord} — {money(needMore, 0)} under your {money(f.buffer, 0)} floor. Set a weekly allocation to see a timeline.</>)
    : tone === 'warn'
      ? <>{priceLead} · {payWord} — doable, but it lands close to your {money(f.buffer, 0)} floor.{payback ? ` Pays for itself in ~${payback.toFixed(1)} months.` : ''}</>
      : <>{priceLead} · {payWord} — comfortably within your cash on hand.{payback ? ` Pays for itself in ~${payback.toFixed(1)} months.` : ''}</>;

  const doSave = async () => {
    if (!title.trim()) return;
    const ok = await onSave({
      type: 'afford', title: title.trim(), inputs: { ...f, title: title.trim() },
      savedCash: cash?.total ?? null, savedVerdict: vTitle,
    });
    if (ok) setSaved(true);
  };

  return (
    <ToolLayout isMobile={isMobile}
      form={(
        <Card pad={isMobile ? '20px 18px' : '24px 26px'}>
          <FormHead>What are you buying?</FormHead>
          <Field label="The thing"><Text value={f.title} onChange={(v: any) => set('title', v)} placeholder="e.g. Capsule filling machine" /></Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 18 }}>
            <Field label="One-time cost"><Num value={f.cost} onChange={(v: any) => set('cost', v)} step={100} /></Field>
            <Field label="Payment"><Select value={f.pay} onChange={(v: any) => set('pay', v)} options={[{ v: 'full', l: 'Pay in full' }, { v: 'deposit', l: 'Deposit + balance' }]} /></Field>
          </div>
          {f.pay === 'deposit' && (
            <div style={{ marginTop: 18 }}>
              <Field label="Deposit now" hint={`Due now: ${money(f.cost * f.depositPct / 100, 0)} · balance later`}><Num value={f.depositPct} onChange={(v: any) => set('depositPct', v)} prefix="" suffix="% now" step={5} /></Field>
            </div>
          )}
          <div style={{ marginTop: 18 }}>
            <Field label="Fund it from"><Select value={f.fundFrom} onChange={(v: any) => set('fundFrom', v)} options={bucketOpts} /></Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 18 }}>
            <Field label="Keep a floor of" hint="Min cash to never dip below"><Num value={f.buffer} onChange={(v: any) => set('buffer', v)} step={1000} /></Field>
            <Field label="Ongoing cost" hint="optional"><Num value={f.ongoing} onChange={(v: any) => set('ongoing', v)} suffix="/mo" step={50} /></Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 18 }}>
            <Field label="Monthly benefit" hint="added revenue or savings"><Num value={f.benefit} onChange={(v: any) => set('benefit', v)} suffix="/mo" step={100} /></Field>
            <Field label="Can allocate" hint="toward it, if saving up"><Num value={f.allocPerWeek} onChange={(v: any) => set('allocPerWeek', v)} suffix="/wk" step={100} /></Field>
          </div>
        </Card>
      )}
      result={(
        <>
          <CashStrip cash={cash} loading={loading} />
          <RecheckBanner seedMeta={seedMeta} verdict={vTitle} cash={cash} />
          <Card pad={isMobile ? '20px 18px' : '22px 24px'}>
            <VerdictCard tone={tone} title={vTitle} detail={vDetail} />
            <div style={{ marginTop: 8 }}>
              <Line k="Item price" v={money(f.cost, 0)} />
              <Line k="Payment" v={f.pay === 'full' ? 'Pay in full' : `${f.depositPct}% now · ${money(dueNow, 0)}`} />
              <Line k="Safety floor" v={money(f.buffer, 0)} last={payback == null} />
              {payback != null && <Line k="Payback" v={`${payback.toFixed(1)} mo`} last />}
            </div>
            <SaveBar title={title} setTitle={setTitle} onSave={doSave} saved={saved} label="Save" />
          </Card>
        </>
      )}
    />
  );
}

// ---- Tool 2: New Product ----------------------------------------------------
const PRODUCT_DEFAULTS = { title: '', price: 39, cogs: 4, feePct: 2.7, feeFlat: 0.30, fulfillment: 3.5, packaging: 1.2, unitsPerMonth: 200, firstRun: 3000, tooling: 500, creative: 400, launchAds: 1500, buffer: 10000 };
function ProductTool({ cash, loading, seed, seedMeta, onSave }: any) {
  const t = useT(); const isMobile = useIsMobile();
  const [f, setF] = React.useState<any>(seed || PRODUCT_DEFAULTS);
  const [title, setTitle] = React.useState<string>(seed?.title || '');
  const [saved, setSaved] = React.useState(false);
  const set = (k: string, v: any) => { setF((p: any) => ({ ...p, [k]: v })); setSaved(false); };

  const fees = f.price * (f.feePct / 100) + f.feeFlat;
  const perUnit = f.cogs + fees + f.fulfillment + f.packaging;
  const margin = f.price - perUnit;
  const marginPct = f.price > 0 ? (margin / f.price) * 100 : 0;
  const monthly = margin * f.unitsPerMonth;
  const upfront = f.firstRun + f.tooling + f.creative + f.launchAds;
  const breakEvenUnits = margin > 0 ? Math.ceil(upfront / margin) : null;
  const payback = monthly > 0 ? upfront / monthly : null;
  const affordLaunch = (cash?.total || 0) - upfront >= f.buffer;

  const tone = margin <= 0 ? 'bad' : (marginPct >= 30 && payback != null && payback <= 9) ? 'good' : (marginPct >= 15 && payback != null && payback <= 18) ? 'warn' : 'bad';
  const vTitle = margin <= 0 ? 'Loses money per unit' : tone === 'good' ? 'Strong — worth building' : tone === 'warn' ? 'Workable — watch the margin' : 'Thin — reconsider';
  const vDetail = margin <= 0
    ? <>Each unit loses <b style={{ color: '#e6dfff' }}>{money(-margin)}</b>. Raise price or cut cost before launching.</>
    : <><b style={{ color: '#e6dfff' }}>{money(margin)} margin/unit ({marginPct.toFixed(0)}%)</b>. At {f.unitsPerMonth}/mo that&apos;s {money(monthly, 0)}/mo — recoups the {money(upfront, 0)} upfront in ~{payback != null ? payback.toFixed(1) : '∞'} months ({breakEvenUnits != null ? breakEvenUnits.toLocaleString() : '—'} units). Launch is {affordLaunch ? 'affordable now' : 'NOT affordable with your floor'}.</>;

  const doSave = async () => {
    if (!title.trim()) return;
    const ok = await onSave({
      type: 'product', title: title.trim(), inputs: { ...f, title: title.trim() },
      savedCash: cash?.total ?? null, savedVerdict: vTitle,
    });
    if (ok) setSaved(true);
  };

  return (
    <ToolLayout isMobile={isMobile}
      form={(
        <Card pad={isMobile ? '20px 18px' : '24px 26px'}>
          <FormHead>Unit economics</FormHead>
          <Field label="Product"><Text value={f.title} onChange={(v: any) => set('title', v)} placeholder="e.g. Gut Reset 30-Day" /></Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 18 }}>
            <Field label="Sell price"><Num value={f.price} onChange={(v: any) => set('price', v)} step={1} /></Field>
            <Field label="COGS / unit" hint="materials or finished cost"><Num value={f.cogs} onChange={(v: any) => set('cogs', v)} step={0.25} /></Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr', gap: 16, marginTop: 18 }}>
            <Field label="Fulfillment"><Num value={f.fulfillment} onChange={(v: any) => set('fulfillment', v)} step={0.25} /></Field>
            <Field label="Packaging"><Num value={f.packaging} onChange={(v: any) => set('packaging', v)} step={0.25} /></Field>
            <Field label="Pay fee %"><Num value={f.feePct} onChange={(v: any) => set('feePct', v)} prefix="" suffix="%" step={0.1} /></Field>
          </div>
          <div style={{ marginTop: 18 }}>
            <Field label="Expected volume"><Num value={f.unitsPerMonth} onChange={(v: any) => set('unitsPerMonth', v)} prefix="" suffix="units/mo" step={25} /></Field>
          </div>
          <div style={{ height: 1, background: t.border, margin: '22px 0 20px' }} />
          <FormHead>Upfront to launch</FormHead>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Field label="First inventory run"><Num value={f.firstRun} onChange={(v: any) => set('firstRun', v)} step={250} /></Field>
            <Field label="Tooling / setup"><Num value={f.tooling} onChange={(v: any) => set('tooling', v)} step={100} /></Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 18 }}>
            <Field label="Creative / photos"><Num value={f.creative} onChange={(v: any) => set('creative', v)} step={100} /></Field>
            <Field label="Launch ad budget"><Num value={f.launchAds} onChange={(v: any) => set('launchAds', v)} step={250} /></Field>
          </div>
        </Card>
      )}
      result={(
        <>
          <CashStrip cash={cash} loading={loading} />
          <RecheckBanner seedMeta={seedMeta} verdict={vTitle} cash={cash} />
          <Card pad={isMobile ? '20px 18px' : '22px 24px'}>
            <VerdictCard tone={tone} title={vTitle} detail={vDetail} />
            {/* the economics at a glance, before the full breakdown */}
            <QuickRef items={[{ label: 'Unit price', value: money(f.price) }, { label: 'All-in cost', value: money(perUnit) }]} />
            <div style={{ marginTop: 8 }}>
              <Line k="Margin / unit" v={`${money(margin)} (${marginPct.toFixed(0)}%)`} />
              <Line k="All-in cost / unit" v={money(perUnit)} />
              <Line k="Monthly contribution" v={money(monthly, 0)} />
              <Line k="Upfront needed" v={money(upfront, 0)} />
              <Line k="Break-even" v={breakEvenUnits != null ? `${breakEvenUnits.toLocaleString()} units` : '—'} />
              <Line k="Payback" v={payback != null ? `${payback.toFixed(1)} mo` : '—'} />
              <Line k="Launch affordable?" v={affordLaunch ? 'Yes' : 'No'} last />
            </div>
            <SaveBar title={title} setTitle={setTitle} onSave={doSave} saved={saved} label="Save this product" />
          </Card>
        </>
      )}
    />
  );
}

// ---- Saved list -------------------------------------------------------------
// The card's summary line is derived from the saved inputs at render time (not
// from the `summary` string stamped at save time), so a card written before this
// layout still shows the current figures. Falls back to the stored string if a
// scenario predates the inputs it needs.
//   purchase    → the item's total cost
//   new product → margin % · all-in cost per unit · first inventory run
function summaryOf(s: Scenario): string | null {
  const f = s.inputs;
  if (!f) return null;
  if (s.type === 'afford') return Number.isFinite(f.cost) ? money(f.cost, 0) : null;
  if (!Number.isFinite(f.price)) return null;
  const fees = f.price * ((f.feePct ?? 0) / 100) + (f.feeFlat ?? 0);
  const perUnit = (f.cogs ?? 0) + fees + (f.fulfillment ?? 0) + (f.packaging ?? 0);
  const marginPct = f.price > 0 ? ((f.price - perUnit) / f.price) * 100 : 0;
  return `${marginPct.toFixed(0)}% margin · ${money(perUnit)} all-in/unit · ${money(f.firstRun ?? 0, 0)} first run`;
}

function SavedList({ scenarios, loading, err, onLoad, onDelete }: any) {
  const t = useT();
  const [pendingDel, setPendingDel] = React.useState<Scenario | null>(null);
  if (loading) return (
    <Card pad={22}><div style={{ fontFamily: FIGTREE, fontSize: 13.5, color: t.muted, textAlign: 'center' }}>Loading saved scenarios…</div></Card>
  );
  if (err && !scenarios.length) return (
    <Card pad={22}><div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}><Icon name="alert" size={15} color={t.warn} /><span style={{ fontFamily: FIGTREE, fontSize: 13.5, color: t.fg2 }}>Couldn’t load your saved scenarios. They’re safe — reload to try again.</span></div></Card>
  );
  if (scenarios.length === 0) return (
    <Card pad={22}><div style={{ fontFamily: FIGTREE, fontSize: 13.5, color: t.muted, textAlign: 'center' }}>No saved scenarios yet. Run the numbers on a purchase or a product, name it, and hit <b>Save</b> — it&apos;ll show up here to revisit or delete.</div></Card>
  );
  const isProduct = (s: Scenario) => s.type === 'product';
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {scenarios.map((s: Scenario) => {
        const prod = isProduct(s);
        const tint = prod ? t.purple : t.accent;
        return (
          <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap', background: t.surface, border: '1px solid ' + t.border, borderRadius: t.cardRadius, padding: '18px 22px' }}>
            <span style={{ width: 48, height: 48, borderRadius: 13, background: prod ? 'rgba(185,169,255,0.1)' : 'rgba(248,146,92,0.1)', border: '1px solid rgba(150,130,230,0.14)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon name={prod ? 'tag' : 'cart'} size={20} color={tint} />
            </span>
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: FIGTREE, fontWeight: 800, fontSize: 17, color: t.fg }}>{s.title}</span>
                <span style={{ fontFamily: OSWALD, fontWeight: 700, letterSpacing: '0.06em', fontSize: 10, color: prod ? t.purple : t.muted, background: prod ? 'rgba(185,169,255,0.14)' : 'rgba(150,130,230,0.14)', padding: '3px 10px', borderRadius: 999 }}>{prod ? 'NEW PRODUCT' : 'PURCHASE'}</span>
              </div>
              <div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.muted, marginTop: 5, fontVariantNumeric: 'tabular-nums' }}>{summaryOf(s) || '—'} · saved {parseAt(s.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
              <button onClick={() => onLoad(s)} style={{ cursor: 'pointer', border: '1px solid ' + t.accent, background: 'transparent', color: t.accent, borderRadius: 999, padding: '9px 24px', fontFamily: FIGTREE, fontWeight: 700, fontSize: 13.5 }}>Open</button>
              <DelBtn onClick={() => setPendingDel(s)} />
            </div>
          </div>
        );
      })}
      {pendingDel && (
        <ConfirmDialog
          title="Delete this scenario?"
          body={<>Remove <b style={{ color: t.fg2 }}>{pendingDel.title}</b> from your saved scenarios? This can&apos;t be undone.</>}
          onCancel={() => setPendingDel(null)}
          onConfirm={() => { onDelete(pendingDel.id); setPendingDel(null); }}
        />
      )}
    </div>
  );
}

// Square outline X on a saved card — turns coral on hover.
function DelBtn({ onClick }: any) {
  const t = useT();
  const [h, setH] = React.useState(false);
  return (
    <button title="Delete" onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ width: 40, height: 40, border: '1px solid ' + (h ? 'rgba(255,139,125,0.5)' : 'rgba(150,130,230,0.22)'), borderRadius: 11, background: 'transparent', color: h ? t.bad : '#8f85b5', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'color 140ms, border-color 140ms' }}>
      <Icon name="x" size={16} color="currentColor" strokeWidth={1.9} />
    </button>
  );
}

// ---- View shell -------------------------------------------------------------
export function NewStuffView() {
  const t = useT();
  const isMobile = useIsMobile();
  const { cash, loading } = useCash();
  const [tab, setTab] = React.useState<'afford' | 'product' | 'saved'>('afford');
  const store = useScenarios();
  const scenarios = store.list || [];
  const [loaded, setLoaded] = React.useState<any>(null); // { type, inputs, k, meta }

  const addScenario = (body: any) => store.add(body);
  const delScenario = (id: string) => store.remove(id);
  // Opening a saved scenario restores what was typed, then re-runs the verdict
  // against today's balance — the saved figures ride along only to explain a change.
  const openScenario = (s: Scenario) => {
    setLoaded({ type: s.type, inputs: s.inputs, k: Date.now(), meta: { savedVerdict: s.savedVerdict, savedCash: s.savedCash, savedAt: s.createdAt } });
    setTab(s.type);
  };

  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 1200 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 6 }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <Title size={isMobile ? 24 : 32}>New Stuff</Title>
          <div style={{ fontFamily: FIGTREE, fontSize: 13.5, color: t.muted, marginTop: 5, maxWidth: 720, lineHeight: 1.5 }}>
            Model a purchase or a new product against your real bank balance — then save the ones worth coming back to.
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 6 }}>
        <Seg value={tab} onChange={(v: any) => { setTab(v); }} options={[{ v: 'afford', l: 'Can we afford it?' }, { v: 'product', l: 'New product' }, { v: 'saved', l: 'Saved', badge: store.list ? scenarios.length : undefined }]} />
      </div>

      {tab === 'afford' && <AffordTool key={loaded?.type === 'afford' ? loaded.k : 'a'} cash={cash} loading={loading} seed={loaded?.type === 'afford' ? loaded.inputs : null} seedMeta={loaded?.type === 'afford' ? loaded.meta : null} onSave={addScenario} />}
      {tab === 'product' && <ProductTool key={loaded?.type === 'product' ? loaded.k : 'p'} cash={cash} loading={loading} seed={loaded?.type === 'product' ? loaded.inputs : null} seedMeta={loaded?.type === 'product' ? loaded.meta : null} onSave={addScenario} />}
      {tab === 'saved' && <SavedList scenarios={scenarios} loading={store.list == null && !store.err} err={store.err} onLoad={openScenario} onDelete={delScenario} />}

      <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.faint, lineHeight: 1.7, marginTop: 4 }}>
        Cash in bank is <b style={{ color: t.muted }}>live</b> from QuickBooks, and saved scenarios live on the account — shared across your machines and every admin user. Opening a saved scenario <b style={{ color: t.muted }}>re-runs the verdict against today&apos;s balance</b>, so affordability is never answered from the cash you had the day you saved it.
      </div>
    </div>
  );
}
